import { widgetConfiguration } from '@sgwt-widget/core';
import { SGWTConnectCore } from '@sgwt/connect-core';

// Functions required from @sgwt/connect-core library
export interface ISgwtConnectLibrary {
  discardAuthorization: () => void;
  discardAuthorizationLocally: () => void;
  getAuthHttpHeader?: () => string;         // Removed in v0.4.0
  getAuthorizationHeader?: () => string;    // Added in v0.4.0
  getGrantedScope?: () => string;           // Added in v0.6.0
  getIdTokenClaims?: () => string;          // Added in v0.9.0
  getJWTClaims?: () => string;              // Removed in v0.9.0
  on: (event: string, callback: () => void) => void;
  requestAuthorization: () => void;
}

export function getInternationalizationLabels(
  i18n: any,
  additionalI18n?: any,
  language?: string
): any {
  const defaultI18n: any = { ...i18n.en };
  const currentLanguage: string = (language || 'EN').toLowerCase();
  if (
    i18n.hasOwnProperty(currentLanguage) &&
    additionalI18n.hasOwnProperty(currentLanguage)
  ) {
    return { ...i18n[currentLanguage], ...additionalI18n[currentLanguage] };
  } else if (additionalI18n.hasOwnProperty(currentLanguage)) {
    return { ...defaultI18n, ...additionalI18n[currentLanguage] };
  } else {
    if (i18n.hasOwnProperty(currentLanguage)) {
      return { ...i18n[currentLanguage] };
    } else {
      widgetConfiguration().log(
        `No internationalization labels for "${currentLanguage}" (through "i18n" attribute).`
      );
      return defaultI18n;
    }
  }
}

/**
 * Detect if displayed on an iOS device.
 * https://stackoverflow.com/questions/4460205/detect-ipad-iphone-webview-via-javascript
 */
export const isEmbeddedInIOS = (): boolean => {
  const standalone = (window.navigator as any).standalone;
  const userAgent = window.navigator.userAgent.toLowerCase();
  const safari = /safari/.test(userAgent); // check for webview & not browser
  const ios = /iphone|ipod|ipad/.test(userAgent);
  return ios && !standalone && !safari;
};

/**
 * The version in User Agent may be written "1.2.3" or "1_2_3". Normalize it to "1.2.3"...
 */
const normalizeSemVer = (version: string): string => {
  const convert = (x: string) => (parseInt(x, 10) || 0);
  const array = version.split(/[\._]/); // "1_2_3" -> ["1", "2", "3"]
  // "1.2.3" -> ["1", "2", "3"]
  return `${convert(array[0])}.${convert(array[1])}.${convert(array[2])}`;
};

/**
 * Check if the current browser is an Android browser in a webview. Not sure to work every time...
 * Resources:
 *   - https://developer.chrome.com/multidevice/user-agent#webview_user_agent
 *   - https://github.com/uupaa/UserAgent.js/blob/master/lib/UserAgent.js
 */
export const isEmbeddedInAndroid = (): boolean => {
  const ua = window.navigator.userAgent;
  const android = /Android/.test(ua);
  const chrome = /Chrome/.test(ua);
  if (android && chrome) {
    const version = normalizeSemVer(ua.split('Chrome/')[1].trim().split(/[^\w\.]/)[0]);
    if (parseFloat(version) >= 42) {
      return /; wv/.test(ua);
    } else if (/\d{2}\.0\.0/.test(version)) {
      return true;
    }
    return (window as any).requestFileSystem || (window as any).webkitRequestFileSystem;
  }
  return false;
};

/**
 * Get Claims from SGWTConnect. The function was `getJWTClaims()` until v.0.9.0 when it becomes `getIdTokenClaims()`.
 */
export const getClaimsFromSgwtConnect = (sgwtConnect: SGWTConnectCore | undefined): any | null => {
  if (sgwtConnect) {
    // For v0.8.0 or lower
    if (typeof (sgwtConnect as any).getJWTClaims !== 'undefined') {
      return (sgwtConnect as any).getJWTClaims();
    }
    // For v0.9.0 or higher
    if (typeof (sgwtConnect as any).getIdTokenClaims !== 'undefined') {
      return (sgwtConnect as any).getIdTokenClaims();
    }
  }
  return null;
};

/**
 * Execute a function if the custom element is present in the page, or execute it when it becomes available.
 */
export function executeWhenCustomElementIsPresent(
  customElement: string,
  cb: () => void
) {
  // First check if we found the element in the DOM...
  if (document.querySelector(customElement) !== null) {
    return cb();
  } else if (window.customElements) {
    // ...or if it is defined in customElements...
    if (window.customElements.get(customElement)) {
      return cb();
    } else {
      // ...else, we wait in case it is defined later.
      window.customElements.whenDefined(customElement).then(cb);
    }
  }
}

/**
 * Get the SGWT Connect interface, either from <sgwt-connect> widget or from `window.sgwtConnect` variable.
 */
export function getSgwtConnectLibrary(): ISgwtConnectLibrary | undefined {
  // First, try to retrieve the <sgwt-connect> widget...
  const sgwtConnectWidget = document.querySelector('sgwt-connect');
  if (sgwtConnectWidget && (sgwtConnectWidget as any).sgwtConnect) {
    return (sgwtConnectWidget as any).sgwtConnect;
    // In case the widget is present in the page without sgwtConnect instance, we will check in the window properties.
    // This case should not really happen, since it means that the <sgwt-connect> widget is used in the passive mode,
    // and the instance has not been passed to it. But we never know...
  }
  // Now, find the sgwtConnect instance in window.
  return (window as any).sgwtConnect;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/common/sgwt-widgets-utils.ts